/**
 * KiaanTab — the "✦ Agents" tab (SUN-70): your AI teams (agents) + chat + live work (SUN-55 team cards) + folders.
 * Layout = kiaan-team-v1.html §1: agents (left, grouped by team) · chat (middle,
 * the existing AIPanel) · folders + live work (right).
 *
 * SUN-57: the left list picks who the middle talks to. Kiaan (general chat) = AIPanel (credits);
 * SUN-97: for admins the Kiaan entry is "Kiaan · Manager" = AgentThreadsPanel with SS Manager (no credits);
 * the general chat stays for everyone else and when SS Manager is paused or missing.
 * SUN-81: it is a general AI chat that reads no AURA data, and the label now says so.
 * an agent = AgentThreadsPanel (SUN-70: chat threads, SUN-61 backend). Only manage-agents
 * admins get the agent conversation; everyone else sees the list read-only.
 *
 * Review Focus 2: when GET /api/kiaan/agents fails the banner
 * "Kiaan Work is not reachable. AURA works as normal." shows, the chat still
 * renders, and nothing throws.
 */
import { useEffect, useMemo, useState } from 'react';
import { AIPanel } from '../layout/AIPanel';
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
import {
  kiaanErrorText,
  getAgentActivity,
  getAgentTalk,
  listAgents,
  type AgentTalk,
  type KiaanAgent,
  type KiaanAgentActivity,
  type LiveWork,
} from './kiaanApi';
import { CreateAgentFlow } from './CreateAgentFlow';
import { AgentThreadsPanel } from './AgentThreadsPanel';
import { RequestsWaiting } from './RequestsWaiting';
import { LiveWorkPanel } from './livework/LiveWorkPanel';
import type { KiaanReach } from './KiaanWidget';
import { useKiaanColumns } from './useKiaanColumns';
import { agentHeadline, agentSubheading, groupAgentsByJob } from './agentGroups';

export interface KiaanTabProps {
  role: DashboardRoleKey | null | undefined;
  pathname: string;
  onCollapse: () => void;
  onReach?: (r: KiaanReach) => void;
  canManageAgents: boolean;
  /** SUN-110c: admin/owner — sees "Requests waiting" with Approve / Reject. */
  isAdmin?: boolean;
  onWide?: (wide: boolean) => void;
  /** Dev-mode admins answer "Needs you" in the Live work panel. */
  canAnswer?: boolean;
  onMessageAgent?: (name: string) => void;
  onOpenScreen?: (route: string) => void;
  onOpenCodeMap?: (route: string) => void;
  /** Live work poll interval; tests pass 0. */
  refreshLiveWorkMs?: number;
  /** Agent reply poll interval; tests pass 0. */
  refreshTalkMs?: number;
}

const MANAGER_NAME = 'ss manager';
const STATUS_CLASS: Record<string, string> = { working: 'kw-b', ready: 'kw-g', off: 'kw-o' };

export function KiaanTab({
  role, pathname, onCollapse, onReach, canManageAgents, isAdmin = false, onWide, canAnswer = false, onMessageAgent, onOpenScreen, onOpenCodeMap, refreshLiveWorkMs, refreshTalkMs,
}: KiaanTabProps) {
  const [agents, setAgents] = useState<KiaanAgent[] | null>(null);
  const [down, setDown] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [activity, setActivity] = useState<KiaanAgentActivity | null>(null);
  const [creating, setCreating] = useState(false);
  // SUN-56: ☰ hides a side column; a thin strip with hints stays (mockups/kiaan-collapse).
  const [cols, toggleCol] = useKiaanColumns();
  const [liveWork, setLiveWork] = useState<LiveWork | null>(null);

  useEffect(() => {
    let alive = true;
    listAgents()
      .then((list) => {
        if (!alive) return;
        setAgents(list);
        setDown(null);
        onReach?.('ok');
        // Kiaan (main) first: null = Kiaan main when Kiaan Work lists no main agent.
        const main = list.find((a) => a.main);
        if (main) setSelected((s) => s ?? main.id);
      })
      .catch((err) => {
        if (!alive) return;
        setAgents([]);
        setDown(kiaanErrorText(err));
        onReach?.('down');
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    let alive = true;
    getAgentActivity(selected)
      .then((a) => { if (alive) setActivity(a); })
      .catch(() => { if (alive) setActivity({ folders: [], steps: [] }); });
    return () => { alive = false; };
  }, [selected]);

  const groups = useMemo(() => groupAgentsByJob(agents ?? [], { isAdmin }), [agents, isAdmin]);
  const main = useMemo(() => (agents ?? []).find((a) => a.main), [agents]);
  const talkingTo = useMemo(() => (agents ?? []).find((a) => a.id === selected), [agents, selected]);
  const isMain = !talkingTo || !!talkingTo.main;
  // SUN-97: admins' "Kiaan" entry talks to SS Manager; paused or missing → the old general chat.
  const manager = useMemo(
    () => (agents ?? []).find((a) => !a.main && (a.agentRole === 'manager' || (!a.agentRole && a.name.trim().toLowerCase() === MANAGER_NAME))),
    [agents],
  );
  // SUN-110c: Kiaan · Manager opens for every active user; other agents stay admin-only.
  const managerChat = !!manager && manager.status !== 'paused';
  const mainIsManager = isMain && managerChat;
  const talkAgent = mainIsManager ? manager : !isMain && canManageAgents ? talkingTo : undefined;
  const [managerTalk, setManagerTalk] = useState<AgentTalk | null>(null);
  const managerId = managerChat ? manager?.id : undefined;
  useEffect(() => {
    if (!managerId) { setManagerTalk(null); return; }
    let alive = true;
    getAgentTalk(managerId)
      .then((t) => { if (alive) setManagerTalk(t); })
      .catch(() => { if (alive) setManagerTalk(null); });
    return () => { alive = false; };
  }, [managerId]);
  const needsCount = liveWork?.needs.length ?? 0;
  const workingNow = (liveWork?.agents ?? []).filter((a) => a.state === 'working');

  if (creating) {
    return (
      <CreateAgentFlow
        agents={agents ?? []}
        onClose={() => { setCreating(false); onWide?.(false); }}
        onCreated={(a, open) => { setAgents((list) => [...(list ?? []), a]); if (open) setSelected(a.id); setCreating(false); onWide?.(false); }}
      />
    );
  }

  return (
    <>
      {down && <div className="kw-banner" role="status">{down}</div>}
      <div className={`kw-cols${cols.left ? '' : ' kw-left-off'}${cols.right ? '' : ' kw-right-off'}`}>
        {cols.left ? (
          <aside className="kw-side" aria-label="AI teams">
            <div className="kw-colhead">
              <span>Agents</span>
              <button type="button" className="kw-burger" onClick={() => toggleCol('left')} title="Hide agents" aria-label="Hide agents">☰</button>
            </div>
            <button
              type="button"
              className={`kw-it${isMain ? ' kw-on' : ''}`}
              onClick={() => setSelected(main?.id ?? null)}
              data-testid="kiaan-main"
              data-tour="kiaan-manager"
            >
              <span className="kw-av kw-ai">✦</span> {managerChat ? 'Kiaan · Manager' : 'Kiaan (general chat)'}
              <span className={`kw-st ${down ? 'kw-o' : 'kw-g'}`} />
            </button>
            {canManageAgents && groups.map((g) => (
              <div key={g.key} data-testid={`kiaan-group-${g.key}`}>
                <div className="kw-sec">{g.title}</div>
                {g.agents.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`kw-it${selected === a.id ? ' kw-on' : ''}`}
                    onClick={() => setSelected(a.id)}
                    data-testid="kiaan-agent"
                  >
                    <span className="kw-av kw-ai">{a.name.charAt(0).toUpperCase()}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.name}
                      {agentSubheading(a, agents ?? []) && (
                        <span className="kw-small kw-agent-sub" title={agentSubheading(a, agents ?? [])} data-testid="kiaan-agent-sub">{agentSubheading(a, agents ?? [])}</span>
                      )}
                    </span>
                    <span className={`kw-st ${STATUS_CLASS[a.status ?? 'off']}`} title={a.status ?? 'off'} />
                  </button>
                ))}
              </div>
            ))}
            {canManageAgents && agents && agents.length === 0 && !down && (
              <div className="kw-small" style={{ marginTop: 8 }}>No agents yet.</div>
            )}
            {canManageAgents && (
              <button type="button" className="kw-btn kw-light" style={{ marginTop: 10 }} onClick={() => { setCreating(true); onWide?.(true); }}>
                ＋ New agent
              </button>
            )}
            <div className="kw-small kw-legend" style={{ marginTop: 10 }} data-testid="kw-legend">
              <span><i className="kw-st kw-b" />working</span>
              <span><i className="kw-st kw-g" />ready</span>
              <span><i className="kw-st kw-o" />off</span>
            </div>
          </aside>
        ) : (
          <div className="kw-rail" data-testid="kw-rail-left">
            <button type="button" className="kw-burger" onClick={() => toggleCol('left')} title="Show agents" aria-label="Show agents">☰</button>
            <span className="kw-rail-av" title={`Talking to: ${talkAgent ? talkAgent.name : 'Kiaan'}`}>
              {talkAgent ? talkAgent.name.charAt(0).toUpperCase() : '✦'}
            </span>
          </div>
        )}

        {talkAgent ? (
          <section className="kw-main" aria-label="Chat" data-tour="kiaan-chat">
            {(
              <AgentThreadsPanel
                agentId={talkAgent.id}
                agentName={mainIsManager ? 'Kiaan Manager' : talkAgent.name}
                headline={mainIsManager ? 'Talking to Kiaan · Manager' : agentHeadline(talkAgent, agents ?? [])}
                you={mainIsManager ? managerTalk?.you : undefined}
                creditsPerMessage={mainIsManager ? managerTalk?.creditsPerMessage : undefined}
                onCollapse={onCollapse}
                refreshMs={refreshTalkMs}
              />
            )}
          </section>
        ) : (
          <section className="kw-main" aria-label="Chat" data-tour="kiaan-chat">
            <div className="kw-talk-who" data-testid="talk-kiaan-head">
              <span className="kw-av kw-ai">✦</span>
              <div>
                <b>Talking to Kiaan</b>
                <div className="kw-small">General AI chat · does not read your AURA data · 10 credits per message</div>
              </div>
            </div>
            <div className="kw-chat-host">
              <AIPanel open embedded onClose={onCollapse} role={role} pathname={pathname} />
            </div>
          </section>
        )}

        {!cols.right && (
          <div className="kw-rail kw-rail-r" data-testid="kw-rail-right">
            <button type="button" className="kw-burger" onClick={() => toggleCol('right')} title="Show live work" aria-label="Show live work">☰</button>
            {needsCount > 0 && <span className="kw-rail-badge" title="Needs you">{needsCount}</span>}
            {workingNow.map((a) => <span key={a.id} className="kw-rail-dot" title={`${a.name} is working`} />)}
          </div>
        )}
        {/* Stays mounted while hidden so Live work keeps polling and the strip stays current. */}
        <aside className="kw-right" aria-label="Folders and live work" data-tour="kiaan-live-work" hidden={!cols.right}>
          <div className="kw-colhead">
            <button type="button" className="kw-burger" onClick={() => toggleCol('right')} title="Hide live work" aria-label="Hide live work">☰</button>
            <span>Live work</span>
          </div>
          {isAdmin && <RequestsWaiting />}
          <LiveWorkPanel
            onData={setLiveWork}
            refreshMs={refreshLiveWorkMs}
            canAnswer={canAnswer}
            canPause={canManageAgents}
            onMessageAgent={onMessageAgent}
            onOpenScreen={onOpenScreen}
            onOpenCodeMap={onOpenCodeMap}
          />
          <div className="kw-sec" style={{ marginTop: 14 }}>Folders Kiaan can use</div>
          {activity && activity.folders.length > 0 ? (
            activity.folders.map((f) => (
              <div key={f.name} className="kw-fold">
                📁 {f.name}
                <span className={`kw-pill kw-ml ${f.access === 'read_write' ? 'kw-ok' : f.access === 'read' ? 'kw-wip' : 'kw-no'}`}>
                  {f.access === 'read_write' ? 'read + write' : f.access === 'read' ? 'read' : 'no access'}
                </span>
              </div>
            ))
          ) : (
            <div className="kw-small">No folders yet.</div>
          )}
        </aside>
      </div>
    </>
  );
}
